import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Mail, Lock, ShieldCheck, ArrowRight, ArrowLeft } from "lucide-react";

/**
 * Place-order auth flow. Contact details are taken from Panel 4 of /order — no in-modal capture.
 *   STEP_CHECK    → silently runs /auth/check-email on open and routes to next step
 *   STEP_CREATED  → new account created, prompt the user to check their inbox
 *   STEP_PASSWORD → existing account, ask for password to trigger OTP
 *   STEP_OTP      → enter 6-digit code, then onAuthenticated
 */
const STEP = { CHECK: 0, CREATED: 1, PASSWORD: 2, OTP: 3 };

const PlaceOrderModal = ({ open, onClose, defaultContact, onAuthenticated }) => {
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(STEP.CHECK);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [remaining, setRemaining] = useState(null);

  const contact = {
    name: defaultContact?.name || user?.name || "",
    email: defaultContact?.email || user?.email || "",
    phone: defaultContact?.phone || "",
  };

  const close = useCallback(() => {
    setStep(STEP.CHECK);
    setBusy(false);
    setPassword("");
    setOtp("");
    setRemaining(null);
    onClose?.();
  }, [onClose]);

  const checkEmail = useCallback(async () => {
    if (!contact.email) {
      toast.error("Please enter your contact details on the order page first.");
      close();
      return;
    }
    setBusy(true);
    try {
      // Logged-in as same email? Skip auth.
      if (user && user.email?.toLowerCase() === contact.email.toLowerCase()) {
        onAuthenticated?.(user);
        close();
        return;
      }
      const { data } = await api.post("/auth/check-email", { email: contact.email });
      if (data.exists && data.has_password) {
        setStep(STEP.PASSWORD);
      } else {
        await api.post("/auth/create-and-verify", {
          email: contact.email, name: contact.name, phone: contact.phone,
        });
        setStep(STEP.CREATED);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start sign-in");
      close();
    } finally { setBusy(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.email, contact.name, contact.phone, user]);

  useEffect(() => {
    if (open) checkEmail();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const doRequestOtp = async (e) => {
    e?.preventDefault?.();
    if (!password) { toast.error("Enter your password"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email: contact.email, password });
      setRemaining(typeof data.remaining_requests === "number" ? data.remaining_requests : null);
      toast.success("Login code sent — check your email");
      setStep(STEP.OTP);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally { setBusy(false); }
  };

  const doResendOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email: contact.email, password });
      setRemaining(typeof data.remaining_requests === "number" ? data.remaining_requests : null);
      toast.success("New code sent");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doVerifyOtp = async (e) => {
    e?.preventDefault?.();
    if (!otp || otp.replace(/\D/g, "").length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email: contact.email, code: otp });
      await refresh();
      toast.success("Signed in!");
      onAuthenticated?.(data);
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md" data-testid="place-order-modal">
        <DialogHeader>
          <DialogTitle>
            {step === STEP.CHECK && "Just a moment…"}
            {step === STEP.CREATED && "Verify your email"}
            {step === STEP.PASSWORD && "Sign in to continue"}
            {step === STEP.OTP && "Enter your login code"}
          </DialogTitle>
          <DialogDescription>
            {step === STEP.CHECK && "Checking your account."}
            {step === STEP.CREATED && "We just emailed you a link to set your password."}
            {step === STEP.PASSWORD && `Welcome back! Enter the password for ${contact.email}.`}
            {step === STEP.OTP && `We sent a 6-digit code to ${contact.email}.`}
          </DialogDescription>
        </DialogHeader>

        {step === STEP.CHECK && (
          <div className="flex flex-col items-center py-6" data-testid="po-checking">
            <Loader2 className="h-8 w-8 text-[#06B6D4] animate-spin" />
            <p className="text-sm text-neutral-500 mt-3">{busy ? "Verifying your email…" : "Preparing…"}</p>
          </div>
        )}

        {step === STEP.CREATED && (
          <div className="text-center py-3" data-testid="po-check-email-step">
            <div className="h-14 w-14 mx-auto rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center">
              <Mail className="h-7 w-7 text-emerald-600" />
            </div>
            <p className="mt-4 text-sm text-neutral-700">
              We&apos;ve emailed <strong>{contact.email}</strong> a verification link from <code className="text-xs">no-reply@printad.ph</code>.
              Open it to set your password, then return here.
            </p>
            <button onClick={close} className="btn-outline text-sm mt-5 inline-flex items-center gap-2"
              data-testid="po-check-email-close-button">
              Got it — I&apos;ll come back
            </button>
          </div>
        )}

        {step === STEP.PASSWORD && (
          <form onSubmit={doRequestOtp} className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5" /> Password
              </label>
              <input type="password" className="pav-input mt-1.5" data-testid="po-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoFocus required />
            </div>
            <button type="submit" disabled={busy} data-testid="po-password-submit"
              className="w-full btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Send login code
            </button>
            <button type="button" onClick={close} data-testid="po-password-back"
              className="w-full text-xs text-neutral-500 inline-flex items-center justify-center gap-1.5 hover:text-[#0B1E3F]">
              <ArrowLeft className="h-3 w-3" /> Update email on the order page
            </button>
          </form>
        )}

        {step === STEP.OTP && (
          <form onSubmit={doVerifyOtp} className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" /> 6-digit code
              </label>
              <input type="text" maxLength={6} inputMode="numeric"
                className="pav-input mt-1.5 tracking-[0.5em] text-center text-2xl font-mono"
                data-testid="po-otp-input"
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                autoFocus required />
            </div>
            <button type="submit" disabled={busy} data-testid="po-otp-submit"
              className="w-full btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Verify &amp; continue
            </button>
            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={() => setStep(STEP.PASSWORD)} data-testid="po-otp-back"
                className="text-neutral-500 inline-flex items-center gap-1 hover:text-[#0B1E3F]">
                <ArrowLeft className="h-3 w-3" /> Back
              </button>
              <button type="button" onClick={doResendOtp} disabled={busy || remaining === 0} data-testid="po-otp-resend"
                className="text-[#0B1E3F] font-semibold hover:underline disabled:opacity-50 disabled:no-underline">
                {remaining === 0 ? "Resend locked" : "Resend code"}
              </button>
            </div>
            {remaining !== null && (
              <div className={`rounded-lg px-3 py-2 text-xs text-center ${remaining === 0 ? "bg-red-50 text-red-700 border border-red-200" : remaining === 1 ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-[#F4F6FB] text-neutral-600"}`}
                data-testid="po-otp-remaining-banner">
                {remaining === 0
                  ? "⚠ No more resends allowed. Use the latest code or wait 10 minutes."
                  : remaining === 1
                  ? `⚠ Only ${remaining} resend left before a 10-minute lockout.`
                  : `${remaining} more resends available in the next 10 minutes.`}
              </div>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlaceOrderModal;
